import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';
import {
  Container,
  Table,
  Button,
  Alert,
  Badge,
  ProgressBar,
  Row,
  Col,
  Card,
  Form
} from 'react-bootstrap';

/**
 * 預算檢視表組件
 * 顯示各科目的預算、實際入出帳和達成率
 */
function BudgetProgress() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 權限控制：判斷是否為管理員或秘書
  const isAdmin = user?.e_position === 17 || isSuperAdmin(user?.e_id);

  // 狀態管理
  const [progress, setProgress] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 篩選條件
  const [ssid, setSsid] = useState(user?.e_ssid || '');
  const [termId, setTermId] = useState('');
  const [societies, setSocieties] = useState([]);
  const [terms, setTerms] = useState([]);

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async (targetSsid = null, autoSelect = true) => {
    try {
      const useSsid = targetSsid || ssid;
      if (!useSsid) {
        console.log('載入屆別失敗：缺少社別 ID');
        return;
      }

      const params = { ssid: useSsid };

      console.log('載入屆別，社別 ID:', useSsid);

      const response = await axios.get('/finance/terms', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const termsData = response.data.data;
        setTerms(termsData);

        // 如果需要自動選擇且有屆別資料
        if (autoSelect && termsData.length > 0) {
          // 優先選擇 is_active = 1 的屆別
          const activeTerm = termsData.find(term => term.is_active === 1 || term.is_active === '1');
          if (activeTerm) {
            console.log('預算檢視表 - 自動選擇啟用屆別:', activeTerm.term_name);
            setTermId(activeTerm.term_id);
          } else {
            // 如果沒有啟用的屆別，選擇最新的（第一個）
            console.log('預算檢視表 - 沒有啟用屆別，選擇第一個:', termsData[0].term_name);
            setTermId(termsData[0].term_id);
          }
        }
      }
    } catch (err) {
      console.error('載入屆別失敗:', err);
    }
  };

  // 載入預算進度
  const loadProgress = async () => {
    if (!ssid || !termId) {
      setProgress([]);
      setSummary(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('載入預算進度，參數:', { ssid, termId });

      // 載入各科目達成率
      const progressResponse = await axios.get('/finance/budgets/progress', {
        params: { ssid, term_id: termId },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('進度回應:', progressResponse.data);

      if (progressResponse.data.success) {
        setProgress(progressResponse.data.data);
      }

      // 載入社別總覽
      const summaryResponse = await axios.get('/finance/budgets/summary', {
        params: { ssid, term_id: termId },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('總覽回應:', summaryResponse.data);

      if (summaryResponse.data.success && summaryResponse.data.data.length > 0) {
        setSummary(summaryResponse.data.data[0]);
      }

    } catch (err) {
      console.error('載入失敗:', err);
      setError('載入預算進度失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入社別列表
  useEffect(() => {
    loadSocieties();
  }, []);

  // 當使用者資訊載入完成後，自動設定並載入屆別
  useEffect(() => {
    if (user?.e_ssid && !termId) {
      console.log('使用者載入完成 - 自動設定社別並載入屆別:', user.e_ssid);
      // 確保社別已設定
      if (ssid !== user.e_ssid) {
        setSsid(user.e_ssid);
      }
      // 直接傳入 user.e_ssid 載入該社別的屆別並自動選擇啟用的屆別
      loadTerms(user.e_ssid, true);
    }
  }, [user?.e_ssid]);

  // 當社別改變時載入屆別（手動切換時）
  useEffect(() => {
    // 只有當社別被手動改變時才重新載入
    if (ssid && user?.e_ssid && ssid !== user.e_ssid) {
      console.log('社別被手動改變 - 重新載入屆別:', ssid);
      setTermId('');
      setTerms([]);
      loadTerms(ssid, true);
    }
  }, [ssid]);

  // 當篩選條件改變時重新載入
  useEffect(() => {
    if (ssid && termId) {
      loadProgress();
    }
  }, [ssid, termId]);

  // 取得進度條顏色
  const getProgressColor = (percent) => {
    if (percent >= 100) return 'success';
    if (percent >= 75) return 'info';
    if (percent >= 50) return 'warning';
    return 'danger';
  };

  // 格式化金額
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">💰 財務管理 - 預算檢視表</h2>

      {/* 錯誤訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      {/* 篩選工具列 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  社別 <span className="text-danger">*</span>
                  {!isAdmin && <small className="text-muted ms-2">(僅可查看本社預算)</small>}
                </Form.Label>
                <Form.Select
                  value={ssid}
                  onChange={(e) => setSsid(e.target.value)}
                  disabled={!isAdmin}
                >
                  <option value="">請選擇社別</option>
                  {societies.map(society => (
                    <option key={society.ss_id} value={society.ss_id}>
                      {society.ss_name}
                    </option>
                  ))}
                </Form.Select>
                {!isAdmin && (
                  <Form.Text className="text-info">
                    <i className="fas fa-info-circle me-1"></i>
                    您目前只能查看所屬社別的預算資訊
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>屆別 <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={termId}
                  onChange={(e) => setTermId(e.target.value)}
                  disabled={!ssid}
                >
                  <option value="">請選擇屆別</option>
                  {terms.map(term => (
                    <option key={term.term_id} value={term.term_id}>
                      {term.term_name}（第 {term.term_no} 屆）
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 總覽統計卡片 */}
      {summary && (
        <Row className="mb-4">
          <Col md={6}>
            <Card bg="success" text="white">
              <Card.Body>
                <Card.Title>收入總預算</Card.Title>
                <h3>{formatAmount(summary.budget_income)}</h3>
                <hr />
                <div className="d-flex justify-content-between">
                  <span>實際收入: {formatAmount(summary.actual_income)}</span>
                  <Badge bg="light" text="dark">{summary.income_progress_pct}%</Badge>
                </div>
                <ProgressBar
                  now={summary.income_progress_pct}
                  variant={getProgressColor(summary.income_progress_pct)}
                  className="mt-2"
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card bg="danger" text="white">
              <Card.Body>
                <Card.Title>支出總預算</Card.Title>
                <h3>{formatAmount(summary.budget_expense)}</h3>
                <hr />
                <div className="mb-2">
                  <div className="d-flex justify-content-between">
                    <span>實際支出: {formatAmount(summary.actual_expense)}</span>
                    <Badge bg="light" text="dark">{summary.expense_progress_pct}%</Badge>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span>已申請未付款: {formatAmount(summary.pending_expense)}</span>
                    <Badge bg="warning" text="dark">
                      {((summary.pending_expense / summary.budget_expense) * 100).toFixed(2)}%
                    </Badge>
                  </div>
                </div>
                <ProgressBar className="mt-2">
                  <ProgressBar
                    variant="light"
                    now={summary.expense_progress_pct}
                    label={`${summary.expense_progress_pct}%`}
                    key={1}
                  />
                  <ProgressBar
                    variant="warning"
                    now={summary.expense_total_usage_pct - summary.expense_progress_pct}
                    key={2}
                  />
                </ProgressBar>
                <small className="mt-1 d-block">
                  總使用率: {summary.expense_total_usage_pct}%
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 各科目達成率列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : !ssid || !termId ? (
        <Alert variant="info">
          請選擇社別和屆別以查看預算進度
        </Alert>
      ) : progress.length === 0 ? (
        <Alert variant="warning">
          此社別和屆別尚無預算資料
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '100px' }}>科目代碼</th>
              <th>科目名稱</th>
              <th style={{ width: '80px' }}>類型</th>
              <th style={{ width: '120px' }}>預算金額</th>
              <th style={{ width: '120px' }}>已申請未付款</th>
              <th style={{ width: '120px' }}>實際金額</th>
              <th style={{ width: '300px' }}>達成率</th>
            </tr>
          </thead>
          <tbody>
            {progress.map(item => {
              const actualAmount = item.s_type === 'income' ? item.actual_income : item.actual_expense;
              const pendingAmount = item.pending_amount || 0;
              const progressPct = item.progress_pct || 0;
              const totalUsagePct = item.total_usage_pct || 0;

              return (
                <tr key={`${item.ssid}-${item.term_id}-${item.s_id}`}>
                  <td><code>{item.s_code}</code></td>
                  <td>{item.s_title}</td>
                  <td>
                    <Badge bg={item.s_type === 'income' ? 'success' : 'danger'}>
                      {item.s_type === 'income' ? '收入' : '支出'}
                    </Badge>
                  </td>
                  <td className="text-end">{formatAmount(item.amount_budgeted)}</td>
                  <td className="text-end">
                    {item.s_type === 'expense' && pendingAmount > 0 ? (
                      <Badge bg="warning" text="dark">{formatAmount(pendingAmount)}</Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-end">{formatAmount(actualAmount)}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div style={{ flex: 1, marginRight: '10px' }}>
                        <ProgressBar>
                          <ProgressBar
                            variant={getProgressColor(progressPct)}
                            now={progressPct}
                            label={`${progressPct}%`}
                            key={1}
                          />
                          <ProgressBar
                            variant="warning"
                            now={totalUsagePct - progressPct}
                            key={2}
                          />
                        </ProgressBar>
                      </div>
                      <small style={{ minWidth: '80px' }} className="text-muted">
                        {progressPct}% + {(totalUsagePct - progressPct).toFixed(2)}%
                      </small>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Container>
  );
}

export default BudgetProgress;
